import AlertsContext from "providers/AlertProvider";
import useRouter from "common/hooks/use-router";
import useItem from "hooks/useItem";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useAuth from "hooks/useAuth";
import type { Zone } from "types";
import PageLoading from "common/components/PageLoading/PageLoading";
import Icon from "components/Icon/Icon";
import { useTranslation } from "react-i18next";
import StickyWrapper from "common/components/StickyWrapper/StickyWrapper";
import MapField from "common/components/MapField/MapField";

function ZoneDetails() {
  const router = useRouter();
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const { user: currentUser } = useAuth();

  const id = router.query.id as string;

  const { item, error, isFetching } = useItem<Zone>("zones", id);

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      router.navigate(-1);
      return;
    }
    router.navigate("/zones");
  }, [router]);

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const zoneData = (item as Zone) || {};
  const {
    name,
    description,
    lat,
    lng,
    city,
    cooperative,
    referent,
    firstRainHistoryDate,
    lastRainHistoryDate,
    lastForecastDate,
  } = zoneData;

  if (isFetching && !item) {
    return <PageLoading />;
  }

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
      <StickyWrapper isHeader disabledOnMobile>
        <div className="border rounded-lg border-slate-200 bg-white p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex justify-center items-center w-8 h-8 duration-200 ease-in-out relative right-0 hover:right-2"
                >
                  <Icon name="ChevronLeftIcon" className="w-6 h-6" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-primary">
                    Lieu N° {id}
                  </h1>
                  <p className="text-lg text-slate-600 mt-1">{name}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StickyWrapper>

      <div className="flex flex-col lg:flex-row gap-4 mt-4">
        <div className="flex-[2] flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-[2] white-box flex flex-col gap-3">
              <div className="flex flex-row gap-6">
                <div className="flex-1">
                  <p className="text-xs text-slate-400 uppercase font-semibold">{t("zones.city")}</p>
                  <p className="text-slate-700">{city || "-"}</p>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-400 uppercase font-semibold">{t("zones.cooperative")}</p>
                  <p className="text-slate-700">{cooperative || "-"}</p>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-400 uppercase font-semibold">{t("zones.referent")}</p>
                  <p className="text-slate-700">{referent || "-"}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold">{t("zones.description")}</p>
                <p className="text-slate-600 whitespace-pre-wrap">{description || "-"}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4">
            <div className="white-box flex flex-col gap-3 flex-1">
              <h3 className="text-sm font-semibold text-primary">{t("zones.firstRainHistoryDate")}</h3>
              <p className="text-slate-700">{firstRainHistoryDate || "-"}</p>
              <h3 className="text-sm font-semibold text-primary">{t("zones.lastRainHistoryDate")}</h3>
              <p className="text-slate-700">{lastRainHistoryDate || "-"}</p>
              <h3 className="text-sm font-semibold text-primary">{t("zones.lastForecastDate")}</h3>
              <p className="text-slate-700">{lastForecastDate || "-"}</p>
            </div>
            <div className="white-box flex-1">
              <div className="flex flex-col lg:flex-row gap-4 lg:items-start">
                <div className="flex-1">
                  <MapField
                    position={{
                      lat: lat ?? undefined,
                      lng: lng ?? undefined,
                    }}
                    editable={false}
                    mapPrefix={`zoneDetails_${id}`}
                    noMargin
                    staticMapHeight={160}
                    staticMapOptions={{
                      disableDefaultUI: true,
                      scrollwheel: true,
                      gestureHandling: "greedy",
                      fullscreenControl: true,
                      clickableIcons: false,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ZoneDetails;
